// Define all shared TypeScript types for Lattice MVP.

export interface ClioMatter {
  initialized?: boolean;
  id: string;
  display_number: string;
  description: string;
  client: string;
  practice_area: string;
  open_date: string;
  status: string;
  responsible_attorney?: ClioContact;
}

export interface ClioDocument {
  id: string;
  name: string;
  category: string;
  created_at: string;
  updated_at?: string;       // ISO timestamp of last modification
  created_by?: string;       // Display name of the Clio user who uploaded
  size_kb?: number;          // File size in KB (for display)
  deeplink: string;
  parent?: {
    id: string;
    name?: string;           // Folder name when available
    type: 'Folder' | 'Matter';
  };
}

export interface ClioFolder {
  id: string;
  name: string;
  parent?: {
    id: string;
    type: 'Folder' | 'Matter';
  };
}

export interface ClioTask {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed';
  due_date?: string;
  assignee?: string;
  assigner?: string;         // User who assigned the task
  created_by?: string;       // User who created the task
  clio_id?: string;
}

export interface ClioContact {
  id: string;
  name: string;
  type: string; // 'Individual' | 'Company'
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  company_id?: string;
  tags?: string[];
  clio_user_id?: string;
  clio_user_enabled?: boolean;
  clio_user_roles?: string[];
  clio_user_subscription_type?: string;
  clio_user_job_title?: string;
}

export interface TaxonomyTerm {
  id?: string;
  label: string;
}

export type ExpertSide = 'our_side' | 'opposing_side' | 'neutral' | 'unknown';
export type ExpertRole = 'consulting' | 'testifying' | 'hybrid' | 'treating_provider' | 'court_appointed' | 'rebuttal';
export type ExpertEngagementStatus = 'candidate' | 'screening' | 'conflict_pending' | 'cleared' | 'terms_pending' | 'retained' | 'declined' | 'withdrawn' | 'replaced' | 'closed';
export type ExpertWorkStatus = 'not_started' | 'awaiting_materials' | 'analyzing' | 'report_expected' | 'report_received' | 'disclosed' | 'deposed' | 'trial_ready' | 'testified' | 'complete';
export type ExpertVisibility = 'case_team' | 'attorneys_only' | 'produced';

export interface Expert {
  id: string;
  clio_contact_id: string;
  contact_snapshot: {
    name: string;
    email?: string;
    phone?: string;
    organization?: string;
  };
  profile_status: 'unverified' | 'active' | 'do_not_use' | 'archived';
  expert_type: 'individual' | 'organization';
  organization_contact_id?: string;
  location?: Record<string, string>;
  service_regions: string[];
  disciplines: TaxonomyTerm[];
  specialties: TaxonomyTerm[];
  languages: string[];
  qualifications: Record<string, unknown>;
  experience: Record<string, unknown>;
  internal_assessment?: Record<string, unknown>;
  tags: string[];
  source: 'manual' | 'clio_relationship' | 'opposing_disclosure' | 'import';
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  rev: number;
}

export interface ExpertIssueAssignment {
  id: string;
  label: string;
  target: {
    type: 'claim' | 'element' | 'fact' | 'damage' | 'motion' | 'custom';
    id: string;
  };
  opinion_scope?: string;
  priority: 'critical' | 'important' | 'supporting';
  allocation_role: 'primary' | 'backup' | 'adverse' | 'neutral';
  status: 'uncovered' | 'candidate' | 'allocated' | 'completed';
}

export interface ExpertDeadlineRef {
  id: string;
  kind: string;
  label: string;
  due_at?: string;
  clio_calendar_id?: string;
  clio_task_id?: string;
  source?: string;
  status?: string;
}

export interface ExpertEngagement {
  id: string;
  matter_id: string;
  expert_id: string;
  side: ExpertSide;
  sponsor_party_ids: string[];
  role: ExpertRole;
  engagement_status: ExpertEngagementStatus;
  work_status: ExpertWorkStatus;
  issue_assignments: ExpertIssueAssignment[];
  competition_group_ids: string[];
  scope_summary?: string;
  conflict_check?: Record<string, unknown>;
  retention?: Record<string, unknown>;
  fee_schedule?: Record<string, unknown>;
  allocation?: Record<string, unknown>;
  deadline_refs: ExpertDeadlineRef[];
  clio_task_ids: string[];
  visibility: ExpertVisibility;
  source: 'manual' | 'opposing_disclosure' | 'court_order' | 'import';
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  archived_at?: string;
  archived_by?: string;
  rev: number;
}

export interface ExpertMaterial {
  id: string;
  matter_id: string;
  engagement_id: string;
  report_id?: string;
  direction: 'provided_to_expert' | 'received_from_expert' | 'disclosed_to_opponent' | 'received_from_opponent';
  item_ref: {
    type: 'evidence' | 'clio_document' | 'fact_snapshot' | 'other';
    id?: string;
    name?: string;
    deeplink?: string;
    label?: string;
  };
  purpose?: string;
  privilege: 'work_product' | 'attorney_client' | 'confidential' | 'produced' | 'public' | 'unknown';
  sent_or_received_at: string;
  sent_or_received_by?: string;
  delivery_method?: string;
  production_set?: string;
  bates_range?: string;
  content_fingerprint?: string;
  notes?: string;
  supersedes_id?: string;
  created_at: string;
  created_by: string;
}

export interface ExpertReportVersion {
  id: string;
  version_label: string;
  version_kind: 'draft' | 'final' | 'served' | 'supplement';
  clio_document_ref: { id: string; name: string; deeplink: string };
  evidence_id?: string;
  report_date?: string;
  received_at?: string;
  disclosed_at?: string;
  supersedes_version_id?: string;
  checksum?: string;
  privilege: 'work_product' | 'confidential' | 'produced' | 'public' | 'unknown';
}

export interface ExpertReport {
  id: string;
  matter_id: string;
  engagement_id: string;
  expert_id: string;
  title: string;
  report_type: 'initial' | 'rebuttal' | 'supplemental' | 'joint' | 'affidavit' | 'declaration';
  status: 'expected' | 'draft_received' | 'under_review' | 'final_received' | 'served' | 'supplement_due' | 'superseded' | 'withdrawn' | 'excluded';
  due_date?: string;
  received_at?: string;
  served_at?: string;
  service_method?: string;
  versions: ExpertReportVersion[];
  latest_version_id?: string;
  materials_complete: boolean;
  methodology_summary?: string;
  limitations_summary?: string;
  assumptions_summary?: string;
  source: 'our_expert' | 'opposing_disclosure' | 'court_filing';
  visibility: ExpertVisibility;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  rev: number;
}

export interface ExpertOpinionFactLink {
  fact_id: string;
  relationship: 'supports' | 'undermines' | 'neutral';
  strength: number;
  rationale?: string;
  accepted_by: string;
  accepted_at: string;
}

export interface ExpertReportOpinion {
  id: string;
  matter_id: string;
  report_id: string;
  report_version_id: string;
  expert_id: string;
  text: string;
  opinion_type: 'ultimate' | 'methodology' | 'calculation' | 'assumption' | 'rebuttal' | 'limitation';
  certainty?: 'possible' | 'probable' | 'reasonable_degree' | 'definitive' | 'not_stated';
  page?: string;
  section?: string;
  source_excerpt?: string;
  extraction_status: 'manual' | 'proposed' | 'accepted' | 'rejected';
  source: 'manual' | 'ai_extraction';
  fact_links: ExpertOpinionFactLink[];
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  rev: number;
}

export interface ExpertCostItem {
  id: string;
  matter_id: string;
  engagement_id: string;
  type: 'estimate' | 'retainer' | 'invoice' | 'payment' | 'credit' | 'expense';
  status: 'anticipated' | 'submitted' | 'approved' | 'paid' | 'disputed' | 'void';
  amount: number;
  currency: string;
  incurred_on?: string;
  due_on?: string;
  clio_activity_id?: string;
  clio_document_ref?: { id: string; name: string; deeplink: string };
  notes?: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  rev: number;
}

export interface ExpertEvent {
  id: string;
  matter_id: string;
  engagement_id: string;
  event_type: string;
  track?: 'engagement' | 'work';
  previous?: string;
  next?: string;
  override?: boolean;
  reason?: string;
  metadata?: Record<string, unknown> & { source_document_type?: string; subordination?: { parent_fact_hints?: string[] } };
  created_at: string;
  created_by: string;
}

export interface RelatedContact {
  id: string;
  clio_contact_id: string;
  name: string;
  label: string; // e.g. "Witness", "Expert", "Corporate Representative"
  is_deposition_individual?: boolean;
}

export interface Party {
  id: string;
  name: string;
  role: string;
  type: 'us' | 'them' | 'tp'; // tp = third party
  clio_contact_id?: string | undefined;
  counsel_contact_ids?: string[] | undefined;
  counsel_contact_id?: string | undefined; // legacy alias for the first counsel contact
  paralegal_contact_ids?: string[] | undefined; // copied on correspondence for this party
  related_contacts?: RelatedContact[];
}

export type FactStatus = 'alleged' | 'supported' | 'contested' | 'established';

export type ValenceType =
  | 'StronglySupports'
  | 'Supports'
  | 'LeansToward'
  | 'Disputed'
  | 'Undercuts'
  | 'Rebuts'
  | 'StronglyRebuts';

// Raw pick coming back from the Clio document picker, before it's
// resolved into (or matched against) a pooled Evidence entity.
export interface EvidenceReference {
  clio_doc_id: string;
  label: string;
  deeplink: string;
}

// A comment left by a team member on a Fact or a piece of Evidence.
export interface Comment {
  id: string;
  author_id: string;
  author_name: string;
  text: string;
  created_at: string;
}

// Evidence is a shared, top-level pool entity (proofGraph.evidence).
// Multiple facts can reference the same Evidence by id.
export interface Evidence {
  id: string;
  clio_doc_id: string;
  label: string;
  deeplink: string;
  notes?: string;
  tags?: string[];
  comments?: Comment[];
  metadata?: Record<string, unknown> & { source_document_type?: string; subordination?: { parent_fact_hints?: string[] } };
  created_at?: string;
  updated_at?: string;
}

export type FactCategory =
  | 'liability'
  | 'damages'
  | 'causation'
  | 'mitigation'
  | 'procedural'
  | 'credibility'
  | 'background'
  | 'timeline'
  | 'custom';

export interface FactPerson {
  id: string;
  clio_contact_id: string;
  name: string;
  role: string; // e.g. "Witness", "Plaintiff", "Expert", "Treating Physician"
}

export interface FactPlace {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  type?: 'incident_scene' | 'medical_facility' | 'office' | 'court' | 'residence' | 'business' | 'other';
  coordinates?: { lat: number; lng: number };
}

export interface FactDamageValue {
  id: string;
  label: string;
  amount: number;
  currency: string;
  type: 'economic' | 'non_economic' | 'punitive' | 'statutory' | 'cost' | 'other';
}

export interface CustomField {
  key: string;
  value: string;
}

export interface CustomFieldPreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  fields: { key: string; default_value: string }[];
}

// A reusable set of columns for the Facts quick-edit table. Column ids are
// either a built-in Fact property (for example `status`) or `custom:<key>`.
export interface FactTableColumnTemplate {
  id: string;
  name: string;
  columns: string[];
}

// Fact is a shared, top-level pool entity (proofGraph.facts).
// Elements point at facts by id via Element.fact_links; the same fact
// can be linked from multiple elements, each with its own valence.
export type FactDatePrecision = 'date' | 'datetime' | 'date_range' | 'week' | 'month' | 'quarter' | 'season' | 'year';

export interface Fact {
  id: string;
  text: string;
  status: FactStatus;
  evidence_ids: string[];
  notes?: string;
  tags?: string[];
  comments?: Comment[];
  metadata?: Record<string, unknown> & { source_document_type?: string; subordination?: { parent_fact_hints?: string[] } };
  created_at?: string;
  updated_at?: string;
  // New metadata fields
  category?: FactCategory;
  date_precision?: FactDatePrecision;
  date_period?: string;
  timeline_sort_date?: string;   // manual anchor within a vague period
  timeline_order?: number;
  date_occurred?: string;        // ISO date/datetime
  date_end?: string;             // for date ranges
  time_of_day?: string;          // HH:MM
  person_ids?: string[];         // refs into proofGraph.persons[]
  place_ids?: string[];          // refs into proofGraph.places[]
  damage_values?: FactDamageValue[];
  custom_fields?: CustomField[];
  strength?: number;             // legacy/global proof-reliability rating; element impact belongs on FactLink
}

// A directed relationship between two pooled facts. The parent fact is the
// broader proposition; the child fact supplies a more specific subordinate
// proposition. Keeping these edges at graph level lets a fact have more than
// one parent without duplicating the fact itself.
export interface FactRelationship {
  id: string;
  parent_fact_id: string;
  child_fact_id: string;
  description: string;
}


// An element's reference to a pooled Fact. Direction, impact magnitude, and
// rationale are link-specific because one shared fact can affect several
// elements in materially different ways.
export interface FactLink {
  fact_id: string;
  valence: ValenceType;
  strength?: number;             // 1-10 impact on this specific element
  rationale?: string;
}

export interface Threat {
  level: 'none' | 'low' | 'contested' | 'favored';
  party_label: string;
  pct: number; // 0..100
}

export interface Element {
  id: string;
  name: string;
  standard: string;
  tier: number; // 0..4
  threat: Threat;
  narrative: {
    text: string;
    empty: boolean;
  };
  fact_links: FactLink[];
}

export interface Author {
  id: string;
  name: string;
  initials: string;
  avatar_tone: string;
}

export interface Message {
  id: string;
  author: Author;
  ts: string;
  body: string;
  mentions?: { user_id: string; name: string }[];
  refs?: { node_type: 'element' | 'fact'; node_id: string; label: string }[];
  is_question?: boolean;
  resolved?: boolean;
  clio_task_id?: string;
}

export interface Thread {
  messages: Message[];
}

export interface Claim {
  id: string;
  name: string;
  authority: string[];
  from_party_id: string;
  from_party_ids?: string[] | undefined;
  against_party_id: string;
  against_party_ids?: string[] | undefined;
  side: 'client' | 'adverse' | 'other';
  burden_party_id: string;
  burden_party_ids?: string[] | undefined;
  burden_party_group?: 'plaintiffs' | 'defendants' | 'third_parties' | undefined;
  floor_tier: number; // derived min(element.tier)
  narrative: {
    text: string;
    empty: boolean;
  };
  thread: Thread;
  elements: Element[];
  collapsed?: boolean | undefined;
  source_claim_id?: string | undefined;
  synced_at?: string | undefined;
}

export interface Stage {
  id: string;
  name: string;
  desc: string;
  cadence_days: number;
  terminal: boolean;
  widgets?: WidgetPlacement[];
}

export interface FloatingState {
  id: string;
  name: string;
  resolves_to: string;
}

export interface Template {
  id: string;
  name: string;
  rev: number;
  is_default: boolean;
  stages: Stage[];
  floating_states: FloatingState[];
  claims: {
    id: string;
    name: string;
    authority: string[];
    elements: { id: string; name: string; standard: string }[];
  }[];
  stage_actions: Record<string, { id: string; name: string; writes: string[] }[]>;
  trigger_sets: Record<string, string[]>;
  audiences: {
    available: string[];
    default: string;
  };
}

export interface BrandingPreferences {
  use_custom_branding?: boolean;
  law_firm_name?: string;
  primary_color?: string;
  secondary_color?: string;
  logo_text?: string;
  logo_image?: string; // base64 data URI (e.g. "data:image/png;base64,...")
}

export interface Preferences {
  bar_licenses?: Record<string, Record<string, string>>;
  document_template?: DocumentTemplatePrefs;
  type: 'prefs';
  id: string;
  default_lens: 'ladder' | 'battlefield' | 'split';
  archetype: 'balanced-civil' | 'documentarian' | 'trial-lawyer' | 'motions-practitioner';
  tier_labels: Record<string, string>;
  guidance_dismissed: {
    narrative: boolean;
    configuration: boolean;
  };
  nav_order: string[];
  stage_workspace_defaults?: Record<string, string>; // stage_id -> workspace_template_id
  branding?: BrandingPreferences;
  motion_workflows?: {
    us: MotionWorkflowTemplate;
    other_side: MotionWorkflowTemplate;
  };
  custom_field_presets?: CustomFieldPreset[];
  fact_table_column_templates?: FactTableColumnTemplate[];
  research_provider?: string;
  citation_format?: LegalCitationFormatId;
}

// ─── Workspace Templates ────────────────────────────────────────────────────
// A WorkspaceTemplate is a named widget layout that can be applied to any stage.
// Global templates are system-provided; local templates are user-created.

export type WorkspaceTemplateScope = 'global' | 'local';

export interface WorkspaceTemplate {
  id: string;
  name: string;
  scope: WorkspaceTemplateScope;
  grid_gap: number;
  widgets: WidgetPlacement[];
  description?: string;
  scenario?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface WorkspaceTemplateLibrary {
  templates: WorkspaceTemplate[];
  default_template_id: string;
}

// ─── Negotiation & Mediation Data Types ─────────────────────────────────────

export type OfferStatus = 'pending' | 'presented' | 'accepted' | 'rejected' | 'countered' | 'no_response';
export type OfferMaker = 'us' | 'them' | 'third_party';
export type OfferType = 'demand' | 'offer' | 'counter' | 'settlement';

export interface NegotiationOffer {
  id: string;
  date: string;
  amount: number;
  maker: OfferMaker;
  status: OfferStatus;
  type: OfferType;
  clio_doc_id?: string;
  clio_doc_label?: string;
  clio_doc_deeplink?: string;
  notes?: string;
  workflow_steps?: OfferWorkflowStep[];
}

export interface OfferWorkflowStep {
  id: string;
  action: 'offered' | 'presented_to_client' | 'accepted' | 'rejected' | 'countered' | 'no_response';
  date: string;
  notes?: string;
}

// ─── AI Document Extraction ─────────────────────────────────────────────────

import type { AIDocumentType } from '../lib/documentTaxonomy';
export type { AIDocumentType } from '../lib/documentTaxonomy';

export interface FilingClassification {
  title: string;
  role: 'motion' | 'memorandum' | 'objection' | 'reply' | 'surreply' | 'affidavit' | 'declaration' | 'other';
  stage: '' | 'motion' | 'objection' | 'reply' | 'surreply';
  motion_title: string;
  motion_entry_number: string;
  moving_parties: string[] | null;
  filed_by: string[] | null;
  party_ids: string[] | null;
  occurred_at: string;
  court_entry_number: string;
  filed: boolean;
  authorities: { citation: string; purpose: string; source_location: string }[] | null;
}

export interface AIDocument {
  document_subtype?: string;
  document_modifiers?: string[];
  classification_taxonomy_version?: string;
  filing?: FilingClassification;
  docket_item_id?: string;
  docket_sync_error?: string;
  _key: string;
  matter_id: string;
  source: 'upload' | 'clio';
  clio_doc_id?: string;
  filename: string;
  content_hash: string;
  document_type?: AIDocumentType;
  classification_status?: 'queued' | 'running' | 'complete' | 'overridden' | 'failed';
  classification_confidence?: number;
  classification_reason?: string;
  classification_model?: string;
  classified_at?: string;
  classification_error?: string;
  status: 'pending' | 'extracted' | 'failed';
  uploaded_by: string;
  uploaded_at: string;
  action_history?: {
    action: 'upload' | 'classification' | 'extraction';
    status: string;
    at?: string;
    finished_at?: string;
    job_id?: string;
    error?: string;
  }[];
  available_actions?: ('reclassify' | 'extract' | 'review' | 'view_results')[];
  proposal_counts?: Partial<Record<ExtractionReviewStatus, number>>;
}

export interface ExtractionJob {
  _key: string;
  ai_document_id: string;
  matter_id: string;
  document_type: AIDocumentType;
  prompt_template_id: string;
  model: string;
  status: 'queued' | 'running' | 'complete' | 'failed';
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  started_at: string;
  finished_at?: string;
  error?: string;
  extracted_court?: {
    name?: string;
    system?: 'state' | 'federal' | 'bankruptcy' | 'uspto' | 'other';
    state?: string;
    judge_name?: string;
    clerk_name?: string;
    magistrate_name?: string;
    calendar?: string;
    confidence?: number;
  };
}

export type ExtractionProposalKind = 'fact' | 'claim' | 'defense' | 'counterclaim' | 'argument' | 'evidence_link' | 'fact_status_update' | 'party' | 'case_history_item' | 'context_patch';
export type ExtractionReviewStatus = 'pending' | 'accepted' | 'edited' | 'rejected' | 'merged';

export interface ExtractionDedupInfo {
  candidate_fact_id: string;
  similarity: number;
  reason: string;
}

export interface ExtractionProvenance {
  extraction: {
    method: string;
    model: string;
    prompt_id: string;
    run_id: string;
    date: string;
  };
  verification: {
    status: 'unverified' | 'accepted' | 'rejected' | 'edited';
  };
  confidence: number;
}

export interface ExtractionPayload {
  name?: string; text?: string; title?: string; description?: string;
  status?: string; new_status?: string; kind?: string; occurred_at?: string;
  side?: string; party_type?: string; from_party_label?: string; against_party_label?: string; burden_party_label?: string;
  fact_id_guess?: string; fact_hint?: string; fact_text?: string; evidence_label?: string;
  motion_id?: string; motion_title?: string; analysis?: string; counterargument?: string;
  source_document_type?: string; source_entry_number?: string; target_entry_number?: string;
  clio_contact_id?: string; clio_contact_name?: string;
  elements?: unknown[]; claim_ids?: string[]; element_ids?: string[]; fact_links?: unknown[];
  source_details?: unknown[]; parent_fact_texts?: string[]; depends_on_fact_texts?: string[];
}

export interface ExtractionProposal<TPayload = ExtractionPayload> {
  _key: string;
  job_id: string;
  ai_document_id?: string;
  matter_id: string;
  kind: ExtractionProposalKind;
  payload: TPayload;
  dedup?: ExtractionDedupInfo;
  provenance: ExtractionProvenance;
  review_status: ExtractionReviewStatus;
  reviewed_by?: string;
  reviewed_at?: string;
}

export interface PromptTemplate {
  _key: string;
  document_type: AIDocumentType;
  name: string;
  system_prompt: string;
  user_prompt_template: string;
  model: string;
  active: boolean;
  version: number;
  updated_by?: string;
  updated_at?: string;
}

export type DamageType = 'compensatory' | 'statutory' | 'attorneys_fees' | 'interest' | 'enhanced';

export interface DamageItem {
  id: string;
  claim_id?: string;
  type: DamageType;
  description: string;
  amount_low: number;
  amount_mid: number;
  amount_high: number;
  fact_ids: string[]; // IDs of facts that prove this damage
  evidence_ids?: string[]; // IDs of specific evidence hand-picked to support this damage
  notes?: string;
  multiplier?: number; // for statutory enhanced (e.g. 3x)
  valuation_type?: 'range_hl' | 'range_hml' | 'sum_certain';
  currency?: string; // ISO 4217 code, e.g. 'USD', 'EUR', 'GBP'
}

export interface NegotiationData {
  matter_id: string;
  offers: NegotiationOffer[];
  damages: DamageItem[];
  posture_override?: number | null; // 0–100, null = computed from claim tiers
  demand_doc_id?: string; // Clio doc ID for the demand letter
  demand_doc_label?: string;
  demand_doc_deeplink?: string;
  notes?: string;
  updated_at?: string;
}

export interface MotionEvidenceLink {
  evidence_id: string;
  relationship: 'relies_upon' | 'targets_exclusion' | 'needs_to_obtain';
  status: 'pending' | 'eliminated_by_court_order' | 'admitted' | 'obtained' | 'outstanding' | 'unobtainable';
  notes?: string;
}

// ─── Motion Workflow ────────────────────────────────────────────────────────

export type MotionInitiator = 'us' | 'other_side';

export interface MotionWorkflowStep {
  id: string;
  label: string;
  order: number;
  triggers_court_rules?: boolean; // opens the jurisdiction/trigger docket-preview flow when a motion enters this step
}

export interface MotionWorkflowTemplate {
  initiator: MotionInitiator;
  steps: MotionWorkflowStep[];
}

export type MotionDocCategory =
  | 'motion'
  | 'opposition'
  | 'reply'
  | 'counter_reply'
  | 'memo_of_law'
  | 'exhibit'
  | 'bench_copy'
  | 'podium_outline';

export interface MotionDocumentLink {
  id: string;
  clio_doc_id: string;
  clio_doc_name: string;
  clio_deeplink?: string;
  category: MotionDocCategory;
  linked_at: string;
}

export interface MotionDocketLink {
  trigger_id: string;
  trigger_name: string;
  events: { name: string; date: string; rule_citation: string }[];
  committed_at: string;
}

export type ContextAIQuality = 'low' | 'medium' | 'high';

export interface ContextAIModelTier {
  id: ContextAIQuality;
  label: string;
  model: string;
  context_tokens: number;
  chat_output_tokens: number;
  report_output_tokens: number;
  description: string;
}

export interface ContextAIUIContext {
  active_view: string;
  focused_type?: string;
  focused_id?: string;
  focused_label?: string;
}

export interface ContextAISourceRef {
  type: 'claim' | 'element' | 'fact' | 'evidence' | 'authority' | 'document';
  id: string;
  label?: string;
}

export interface ContextAIAnswerPart {
  text: string;
  classification: 'supported' | 'contested' | 'inference' | 'gap' | 'attorney_theory';
  source_refs: ContextAISourceRef[];
  confidence: number;
}

export interface ContextAIEntityOption {
  id: string;
  type: 'person' | 'contact' | 'create';
  label: string;
  detail?: string;
  clio_contact_id?: string | undefined;
}

export interface ContextAIProposalPayload {
  operation: string;
  target_type: string;
  target_id?: string;
  target_label: string;
  summary: string;
  reason: string;
  changes: Record<string, unknown>;
  source_refs: ContextAISourceRef[];
  confidence: number;
  entity_query?: string;
  entity_options?: ContextAIEntityOption[];
}

export interface ContextAIChatResponse {
  answer: string;
  answer_parts: ContextAIAnswerPart[];
  limitations: string[];
  follow_up_question?: string;
  proposals: (ExtractionProposal & { payload: ContextAIProposalPayload })[];
  model: string;
  quality: ContextAIQuality;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
}

export interface ContextAIReportFinding {
  statement: string;
  classification: 'supported' | 'contested' | 'inference' | 'gap' | 'attorney_theory';
  source_refs: ContextAISourceRef[];
  confidence: number;
  analysis: string;
  contrary_sources: ContextAISourceRef[];
  attorney_action: string;
}

export interface ContextAIReport {
  title: string;
  executive_summary: string;
  sections: { heading: string; purpose: string; findings: ContextAIReportFinding[] }[];
  methodology: string[];
  limitations: string[];
  attorney_questions: string[];
  model: string;
  quality: ContextAIQuality;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
}

export type CaseIntelligenceKind = 'connection' | 'question' | 'deepen';
export type CaseIntelligencePriority = 'high' | 'medium' | 'low';
export type CaseIntelligenceRunStatus = 'queued' | 'running' | 'complete' | 'failed';

export interface CaseIntelligenceSourceRef {
  type: string;
  id: string;
  label?: string;
}

export interface CaseIntelligenceInsight {
  id: string;
  kind: CaseIntelligenceKind;
  priority: CaseIntelligencePriority;
  title: string;
  summary: string;
  rationale: string;
  question?: string;
  suggested_next_step?: string;
  confidence: number;
  source_refs: CaseIntelligenceSourceRef[];
}

export interface CaseIntelligenceInventory {
  total_data_points: number;
  counts: Record<string, number>;
  shard_count: number;
  coverage_complete: boolean;
}

export interface CaseIntelligenceRun {
  _key: string;
  matter_id: string;
  status: CaseIntelligenceRunStatus;
  model: string;
  reasoning_effort: string;
  prompt_version: string;
  input_hash: string;
  inventory: CaseIntelligenceInventory;
  insights: CaseIntelligenceInsight[];
  limitations: string[];
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  error?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export interface CaseIntelligencePreflight {
  model: string;
  reasoning_effort: string;
  prompt_version: string;
  inventory: CaseIntelligenceInventory;
  estimated_input_tokens: number;
  estimated_output_tokens: number;
  estimated_cost_usd: number;
  estimated_credits: number;
  credits_remaining?: number;
  within_monthly_budget: boolean;
  cached_run?: CaseIntelligenceRun;
  external_data_notice: string;
}

export interface MotionArgument {
  id: string;
  matter_id: string;
  motion_id: string;
  title: string;
  side: 'our_side' | 'opposing_side';
  position: 'supports_relief' | 'opposes_relief' | 'reply' | 'alternative';
  requested_relief?: string;
  standard_issue_id?: string;
  issue_ids: string[];
  claim_ids?: string[];
  fact_ids: string[];
  evidence_ids: string[];
  application_ids: string[];
  citation_use_ids: string[];
  status: 'researching' | 'drafting' | 'ready' | 'filed' | 'resolved' | 'abandoned';
  analysis?: string;
  counterargument?: string;
  order: number;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  rev: number;
  source_ai_document_id?: string;
  source_entry_number?: string;
}

export interface Motion {
  id: string;
  name: string;
  type: 'dispositive' | 'non-dispositive';
  category: 'limine' | 'compel' | 'quash' | 'protective_order' | 'other';
  status: 'draft' | 'filed' | 'granted' | 'denied' | 'pending';
  initiator: MotionInitiator;
  workflow_step_id: string;
  documents: MotionDocumentLink[];
  docket_links: MotionDocketLink[];
  evidence_links: MotionEvidenceLink[];
  arguments?: MotionArgument[];
  created_at?: string;
  updated_at?: string;
}

// ─── Authorities MVP ────────────────────────────────────────────────────────
// Matter-scoped legal research projection. The long-term Authorities plan
// moves source text and shared authority records into resource-specific
// collections; the MVP keeps this compact, editable projection in the matter
// graph so it can use the existing optimistic save path.

export interface AuthorityFitFactors {
  hierarchy: number; // 0..30 — jurisdiction and precedential hierarchy
  forum: number; // 0..15 — same judge / court / forum proximity
  holding: number; // 0..25 — holding and rule match
  facts: number; // 0..20 — factual and procedural-posture similarity
  treatment: number; // 0..10 — currentness and treatment confidence
}

export type AuthorityCaseSide = 'support' | 'adverse';
export type LegalCitationFormatId = 'bluebook' | 'alwd' | 'california' | 'new_york' | 'florida' | 'illinois' | 'texas' | 'court_local';
export type AuthorityType =
  | 'case'
  | 'statute'
  | 'regulation'
  | 'court_rule'
  | 'constitutional'
  | 'treatise'
  | 'practice_guide'
  | 'journal'
  | 'administrative_decision'
  | 'restatement'
  | 'jury_instruction'
  | 'brief'
  | 'court_filing'
  | 'order'
  | 'transcript'
  | 'website'
  | 'dataset'
  | 'unpublished_material'
  | 'other';
export type AuthoritySourceKind = 'research_database' | 'downloaded_file' | 'official_source';
export type AuthorityCaseRelationship =
  | 'supports'
  | 'supports_rule'
  | 'supports_application'
  | 'analogous_backup'
  | 'contradicts'
  | 'qualifies'
  | 'limits'
  | 'distinguishable';

export interface AuthorityCase {
  id: string;
  authority_type?: AuthorityType;
  side: AuthorityCaseSide;
  relationship: AuthorityCaseRelationship;
  name: string;
  citation: string;
  court: string;
  authority_weight: string; // Kept separate from computed persuasive fit.
  factors: AuthorityFitFactors;
  quote?: string | undefined;
  pin_cite?: string | undefined;
  fit_rationale?: string;
  verification_note?: string;
  source_kind?: AuthoritySourceKind;
  source_provider?: string;
  source_url?: string;
  file_name?: string;
  file_url?: string;
  passages?: AuthorityPassage[];
  linked_authorities?: LinkedAuthorityCitation[];
  citation_metadata?: AuthorityCitationMetadata;
}

export type AuthorityPublicationStatus = 'published' | 'unpublished' | 'slip_opinion' | 'forthcoming' | 'unreported' | 'electronic_only' | 'manuscript' | 'other';

export interface AuthorityCitationMetadata {
  short_name?: string;
  volume?: string;
  reporter?: string;
  first_page?: string;
  neutral_citation?: string;
  docket_number?: string;
  database_identifier?: string;
  court_abbreviation?: string;
  decision_date?: string;
  decision_year?: string;
  subsequent_history?: string;
  parallel_citations?: string[];
  publication_status?: AuthorityPublicationStatus;
  authors?: string[];
  editors?: string[];
  edition?: string;
  publisher?: string;
  publication_year?: string;
  container_title?: string;
  periodical_volume?: string;
  issue_number?: string;
  page_range?: string;
  code_name?: string;
  section?: string;
  rule_number?: string;
  jurisdiction?: string;
  accessed_at?: string;
  archive_url?: string;
  rendered_citations?: Partial<Record<LegalCitationFormatId, string>>;
  custom_fields?: { label: string; value: string }[];
}

export interface AuthorityPinpoint {
  locator?: string;
  page?: string;
  paragraph?: string;
  section?: string;
  footnote?: string;
  chapter?: string;
  timestamp?: string;
  line?: string;
}

export interface AuthorityPassage {
  id: string;
  quote: string;
  pin_cite?: string | undefined;
  pinpoint?: AuthorityPinpoint;
  citation_format?: LegalCitationFormatId;
  citation_signal?: string;
  explanatory_parenthetical?: string;
  attorney_note?: string | undefined;
  verification_status?: 'unverified' | 'source_checked' | 'attorney_verified' | undefined;
}

export interface LinkedAuthorityCitation {
  id: string;
  authority_type: AuthorityType;
  citation: string;
  title?: string;
  link_role: 'defines_term' | 'incorporates' | 'exception' | 'procedure' | 'amends' | 'interprets' | 'other';
  source_url?: string;
}

export interface AuthorityResearchTarget {
  type: 'claim' | 'element' | 'fact' | 'motion' | 'motion_argument' | 'custom';
  id: string;
  label: string;
}

export interface ResearchPrinciple {
  id: string;
  parent_id?: string | undefined;
  node_type?: 'folder' | 'principle' | undefined;
  sort_order?: number;
  title: string;
  question?: string | undefined;
  coverage_status: 'not_started' | 'researching' | 'needs_review' | 'covered' | 'unresolved';
  targets: AuthorityResearchTarget[];
  lead_case_id?: string | undefined;
  cases: AuthorityCase[];
  created_at?: string;
  updated_at?: string;
}

export interface AuthorityResearchState {
  principles: ResearchPrinciple[];
  is_example?: boolean;
  updated_at?: string;
}

// Attorney-authored strategy is durable matter data. The remaining context
// artifacts are deterministic projections of the proof graph and are rebuilt
// whenever the graph changes, so stale summaries never become a second source
// of truth.
export interface CaseTheory {
  thesis: string;
  themes: string[];
  disputed_issues: string[];
  concessions: string[];
  desired_outcome: string;
  updated_at?: string;
  updated_by?: string;
}

export interface CaseContextState {
  theory: CaseTheory;
  pinned_artifact_ids?: string[];
}

export type CaseContextArtifactKind = 'theory' | 'proof_chain' | 'timeline' | 'gap' | 'source';

export interface CaseContextSourceRef {
  type: 'claim' | 'element' | 'fact' | 'evidence' | 'authority' | 'document';
  id: string;
}

export interface CaseContextArtifact {
  id: string;
  kind: CaseContextArtifactKind;
  title: string;
  summary: string;
  source_refs: CaseContextSourceRef[];
  priority: number;
  estimated_tokens: number;
  date?: string;
  gap_severity?: 'critical' | 'important' | 'watch';
  search_text: string;
}

export interface CaseContextBundle {
  artifacts: CaseContextArtifact[];
  estimated_tokens: number;
  budget_tokens: number;
  omitted_artifacts: number;
}

export interface ProofGraph {
  court_name?: string;
  jurisdiction_id?: string;
  judge_name?: string;
  clerk_name?: string;
  magistrate_name?: string;
  calendar?: string;
  matter_details?: import('../components/matter-details/MatterDetailsPanel').MatterDetailsConfiguration;
  stage_widgets?: Record<string, WidgetPlacement[]>;
  matter_id: string;
  template_id: string;
  active_stage_id: string;
  floating_states: Record<string, boolean>;
  parties: Party[];
  represented_party_ids?: string[];
  claims: Claim[];
  facts: Fact[];
  fact_relationships?: FactRelationship[];
  evidence: Evidence[];
  authority_research?: AuthorityResearchState;
  case_context?: CaseContextState;
  motions?: Motion[];
  strategic_notes?: StrategicNote[];
  calendar_events?: CalendarEvent[];
  persons?: FactPerson[];
  places?: FactPlace[];
}

export interface CalendarEvent {
  id: string;
  title: string;
  start: string; // ISO string 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:MM:SS'
  end?: string;
  type: 'hearing' | 'meeting' | 'deadline' | 'deposition' | 'trial';
  creator: string;
  calendarName: string;
  description?: string;
  location?: string;
}

export interface ClioActivity {
  id: string;
  type: 'TimeEntry' | 'ExpenseEntry';
  quantity: number;
  price: number;
  total: number;
  note: string;
  date: string;
  user: string;
  activity_description: string;
  billable: boolean;
}

export interface StrategicNote {
  id: string;
  author: string;
  date: string;
  content: string; // HTML format rich text
  is_archived?: boolean;
}

export interface WidgetPlacement {
  id: string;
  type: 'action' | 'document' | 'task' | 'cost' | 'calendar' | 'note' | 'template_launch';
  w: number; // column span, 1..6
  h: number; // row span, 1..6
  x?: number; // column origin (0-indexed, 0..5); auto-placed if missing
  y?: number; // row origin (0-indexed); auto-placed if missing
  grid_gap?: number; // workspace spacing persisted with matter layouts
  config?: import('../lib/widgetConfig').WidgetConfig | undefined;
}

export interface StageLayout {
  stage_id: string;
  widgets: WidgetPlacement[];
}

export interface JurisdictionV2 {
  state: string;
  court_system: 'state' | 'federal' | 'all';
  circuit?: string;
  court_id?: string;
  splits?: string;
}

export interface TaxonomyV2 {
  canonical_id?: string;
  nos_code?: string;
  offense_class?: string;
}

export interface GoverningAuthorityV2 {
  authority_type: 'statute' | 'case' | 'regulation' | 'court_rule' | 'jury_instruction' | 'constitution' | 'restatement';
  citation: string;
  pin_cite?: string;
  quote?: string;
  role: 'creates_claim' | 'defines_elements' | 'limits_claim' | 'sets_limitations_period' | 'sets_remedies' | 'pattern_instruction';
  binding?: boolean;
  authority_ref?: string;
}

export interface StandardSourceV2 {
  citation: string;
  pin_cite?: string;
  quote?: string;
  source_doc_id?: string;
}

export interface BurdenV2 {
  party: 'claimant' | 'respondent' | 'prosecution' | 'defendant' | 'shifting' | 'none';
  standard_of_proof: 'preponderance' | 'clear_and_convincing' | 'beyond_reasonable_doubt' | 'probable_cause' | 'substantial_evidence' | 'arbitrary_capricious' | 'prima_facie_then_shifting' | 'unspecified';
  shifting_mechanics?: string;
}

export interface AlternativeV2 {
  id?: string;
  standard?: string;
  standard_source?: StandardSourceV2;
}

export interface SubElementV2 {
  id?: string;
  standard?: string;
  standard_source?: StandardSourceV2;
}

export interface ElementV2 {
  id: string;
  name: string;
  standard: string;
  standard_source?: StandardSourceV2;
  burden: BurdenV2;
  alternatives?: AlternativeV2[];
  sub_elements?: SubElementV2[];
  mens_rea?: 'purposely' | 'knowingly' | 'recklessly' | 'negligently' | 'strict_liability' | 'malice' | 'specific_intent' | 'general_intent' | 'not_applicable';
  notes?: string;
}

export function sanitizeClioDocumentUrl(url: string, docId: string): string {
  if (!url) return `https://app.clio.com/nc/#/documents/${docId}/details`;
  const oldRegex = /https:\/\/app\.clio\.com\/documents\/(\d+)/;
  const match = oldRegex.exec(url);
  if (match?.[1]) {
    return `https://app.clio.com/nc/#/documents/${match[1]}/details`;
  }
  return url;
}

export interface ReposeV2 {
  period?: string;
  statute_cite?: string;
}

export interface NoticePrerequisiteV2 {
  description: string;
  citation: string;
  deadline?: string;
  consequence_of_failure?: 'jurisdictional_bar' | 'dismissal_without_prejudice' | 'abatement' | 'damages_limitation' | 'other';
}

export interface LimitationsV2 {
  period?: string;
  statute_cite?: string;
  accrual?: string;
  discovery_rule?: boolean;
  discovery_rule_notes?: string;
  tolling?: string[];
  repose?: ReposeV2;
  notice_prerequisites?: NoticePrerequisiteV2[];
}

export interface PleadingV2 {
  heightened?: boolean;
  rule_cite?: string;
  particularity_notes?: string;
  verification_required?: boolean;
  certificate_of_merit?: boolean;
  anti_slapp_exposure?: boolean;
}

export interface PunitiveV2 {
  available?: boolean;
  standard_of_proof?: string;
  cap?: string;
  cite?: string;
}

export interface FeeShiftingV2 {
  available?: boolean;
  direction?: 'prevailing_claimant' | 'prevailing_party' | 'discretionary' | 'none';
  cite?: string;
}

export interface RemediesV2 {
  compensatory?: boolean;
  punitive?: PunitiveV2;
  statutory_damages?: string;
  multiple_damages?: string;
  injunctive?: boolean;
  fee_shifting?: FeeShiftingV2;
  prejudgment_interest?: string;
}

export interface ProcedureV2 {
  jury_trial_right?: 'yes' | 'no' | 'equitable_no' | 'mixed' | 'unresolved';
  exhaustion_required?: boolean;
  exhaustion_notes?: string;
  standing_notes?: string;
  immunities?: string[];
  arbitrability?: string;
}

export interface CriminalV2 {
  grading?: string;
  penalty_range?: string;
  enhancements?: string[];
  collateral_consequences_notes?: string;
}

export interface ExtractionV2 {
  method: 'llm_extraction' | 'manual' | 'jury_instruction_parse' | 'statute_parse';
  model?: string;
  date: string;
  prompt_id?: string;
  run_id?: string;
  source_doc_ids?: string[];
  consensus_count?: number;
}

export interface VerificationV2 {
  status: 'unverified' | 'machine_verified' | 'attorney_verified' | 'rejected';
  quote_match?: boolean;
  citation_check?: boolean;
  verifier?: string;
  date?: string;
  review_notes?: string;
}

export interface ProvenanceV2 {
  extraction: ExtractionV2;
  verification: VerificationV2;
  confidence: number;
}

export interface VersioningV2 {
  created: string;
  updated?: string;
  law_current_as_of: string;
  recheck_by?: string;
  status: 'active' | 'superseded' | 'abrogated' | 'repealed' | 'questioned';
}

export interface UniversalClaimV2 {
  scope?: 'personal' | 'firm' | 'master';
  _key?: string;
  schema_version: '2.0';
  name: string;
  aka?: string[];
  posture: 'claim' | 'affirmative_defense' | 'negating_defense' | 'counterclaim_only' | 'charge';
  domain: 'civil' | 'criminal' | 'administrative' | 'bankruptcy' | 'probate' | 'family' | 'tax';
  source_of_law?: 'common_law' | 'statutory' | 'regulatory' | 'constitutional' | 'hybrid';
  jurisdiction: JurisdictionV2;
  taxonomy?: TaxonomyV2;
  governing_authorities: GoverningAuthorityV2[];
  elements: ElementV2[];
  limitations?: LimitationsV2;
  pleading?: PleadingV2;
  remedies?: RemediesV2;
  procedure?: ProcedureV2;
  provenance: ProvenanceV2;
  versioning: VersioningV2;
  notes?: string;
  tags?: string[];
  search_keywords?: string[];
}

// ModalConfigState is the shape of the app-level alert/confirm/prompt modal
// controller shared between App and the components that trigger modals.
export interface ModalConfigState {
  isOpen: boolean;
  title: string;
  message?: string;
  type: 'alert' | 'confirm' | 'prompt';
  promptFields?: import('../components/ModalDialog').PromptField[];
  onConfirm: (values?: Record<string, string>) => void | Promise<void>;
}

export type DocketItemKind = 'docket_filing' | 'undocketed_document' | 'event' | 'text_order';
export type DocketStatus = 'docketed' | 'undocketed' | 'not_applicable';

export interface DocketDocumentRef {
  ai_document_id?: string;
  clio_document_id?: string;
  name?: string;
  deeplink?: string;
  content_hash?: string;
}

export interface DocketMetadataField {
  key: string;
  label: string;
  value_type: string;
  value: unknown;
  source?: string;
}

export interface CaseHistoryItem {
  id: string;
  matter_id: string;
  kind: DocketItemKind;
  docket_status: DocketStatus;
  stage_key: string;
  category: string;
  document_type?: string;
  title: string;
  summary?: string;
  occurred_at: string;
  court_entry_number?: string;
  filed_by?: string;
  direction?: string;
  status?: string;
  event_type?: string;
  location?: string;
  text_order?: string;
  source_document?: DocketDocumentRef;
  metadata: DocketMetadataField[];
  source: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  rev: number;
}

export type DocketRelationType =
  | 'amends' | 'supersedes' | 'responds_to' | 'opposes' | 'replies_to'
  | 'supports' | 'resolves' | 'requests' | 'produces_in_response_to'
  | 'notices' | 'transcript_of' | 'cites' | 'memorializes' | 'encloses' | 'related_to';

export interface CaseHistoryRelation {
  id: string;
  matter_id: string;
  from_item_id: string;
  to_item_id: string;
  type: DocketRelationType;
  label?: string;
  rationale?: string;
  created_at: string;
  created_by: string;
  rev: number;
}

export interface DocketStageConfig {
  id: string;
  matter_id: string;
  label: string;
  order: number;
  start_date?: string;
  end_date?: string;
  color: string;
  enabled: boolean;
  rev: number;
}

export interface DocumentTemplatePrefs {
  fontFamily: string;
  fontSize: number;
  lineSpacing: 'single' | 'one-and-half' | 'double';
  captionStyle: 'vertical-line' | 'closing-bracket' | 'box' | 'none';
  firmName: string;
  firmAddress: string;
  firmPhone: string;
  firmEmail: string;
  defaultServiceText: string;
}

export interface MatterResponse { clio: ClioMatter; tasks: ClioTask[]; activities: ClioActivity[]; calendar_events: CalendarEvent[]; graph: ProofGraph; initialized?: boolean }
export interface NegotiationState { offers?: NegotiationOffer[]; damages?: DamageItem[]; posture_override?: number | null }
export interface ApiErrorBody { message?: string; error?: string }
